import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

type SlidingLayoutCardColor = 'default' | 'purple' | 'saffron' | 'lime' | 'blue';

type SlidingLayoutBrightnessMode = 'light' | 'dark';

type SlidingLayoutColorMode =
	| 'none'
	| 'backgroundColor'
	| 'polygonBackgroundColor'
	| 'borderColor'
	| 'buttonColor';

export type SlidingLayoutCard = {
	title: string;
	color: SlidingLayoutCardColor;
	ctaLinkUrl: string;
	ctaLinkText: string;
	icon?: string;
};

type SlidingLayoutProps = {
	cards: SlidingLayoutCard[];
	brightnessMode: SlidingLayoutBrightnessMode;
	colorMode: SlidingLayoutColorMode;
};

type FeaturedSide = 'left' | 'right';

type SlidingLayoutCardArea = 'featured' | 'smallTop' | 'smallBottom';
type SlidingLayoutSmallCardArea = Exclude<SlidingLayoutCardArea, 'featured'>;
type AnimationParticipant = 'clicked' | 'featured' | 'other';

type AnimationContext = {
	clickedIndex: number;
	featuredIndex: number;
	otherIndex: number;
	clickedRole: SlidingLayoutSmallCardArea;
	otherRole: SlidingLayoutSmallCardArea;
	nextFeaturedSide: FeaturedSide;
};

const SWITCH_DELAY_MS = 2000;
const CONTAINER_GAP_PX = token('space.300');

const intervalFeaturedShrinking = '25%, 100%';
const intervalClickedToFeatured = '25%, 65%';
const intervalOtherCardExpanding = '37%, 50%';
const intervalOtherCardShrinking = '75%, 100%';

const featuredFromLeftToLeftSmallTop = keyframes({
	'0%': {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
	[intervalFeaturedShrinking]: {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const featuredFromLeftToLeftSmallBottom = keyframes({
	'0%': {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
	[intervalFeaturedShrinking]: {
		insetInlineStart: 0,
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const featuredFromRightToRightSmallTop = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 'var(--sl-top-featured)',
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
	[intervalFeaturedShrinking]: {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 'var(--sl-top-small-top)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const featuredFromRightToRightSmallBottom = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 'var(--sl-top-featured)',
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
	[intervalFeaturedShrinking]: {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const smallTopFromLeftSmallToLeftFeatured = keyframes({
	'0%': {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalClickedToFeatured]: {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-small-height)',
	},
	'100%': {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
});

const smallBottomFromLeftSmallToLeftFeatured = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-small-on-left-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalClickedToFeatured]: {
		insetInlineStart: 'var(--sl-left-small-on-left-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-small-height)',
	},
	'100%': {
		insetInlineStart: 'var(--sl-left-featured-left)',
		insetBlockStart: 'var(--sl-top-featured)',
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
});

const smallTopFromRightSmallToRightFeatured = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalClickedToFeatured]: {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-small-height)',
	},
	'100%': {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
});

const smallBottomFromRightSmallToRightFeatured = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalClickedToFeatured]: {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-small-height)',
	},
	'100%': {
		insetInlineStart: 'var(--sl-left-featured-right)',
		insetBlockStart: 0,
		width: 'var(--sl-featured-width)',
		height: 'var(--sl-height-featured)',
	},
});

const smallTopFromLeftToRight = keyframes({
	'0%': {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardExpanding]: {
		animationTimingFunction: wacToken('motion.easing.quick-entrance'),
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: '100%',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardShrinking]: {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const smallBottomFromLeftToRight = keyframes({
	'0%': {
		insetInlineStart: 0,
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardExpanding]: {
		animationTimingFunction: wacToken('motion.easing.quick-entrance'),
		insetInlineStart: 0,
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: '100%',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardShrinking]: {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const smallTopFromRightToLeft = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardExpanding]: {
		animationTimingFunction: wacToken('motion.easing.quick-entrance'),
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: '100%',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardShrinking]: {
		insetInlineStart: 0,
		insetBlockStart: 0,
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const smallBottomFromRightToLeft = keyframes({
	'0%': {
		insetInlineStart: 'var(--sl-left-small-on-right-side)',
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardExpanding]: {
		animationTimingFunction: wacToken('motion.easing.quick-entrance'),
		insetInlineStart: 0,
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: '100%',
		height: 'var(--sl-small-height)',
	},
	[intervalOtherCardShrinking]: {
		insetInlineStart: 0,
		insetBlockStart: 'var(--sl-top-small-bottom)',
		width: 'var(--sl-small-width)',
		height: 'var(--sl-small-height)',
	},
});

const slidingLayoutSectionStyle = css({
	padding: token('space.300'),
	['&.brightness_dark']: {
		backgroundColor: wacToken('color.primary.black'),
	},
	['&.brightness_light']: {
		backgroundColor: wacToken('color.neutral.light0'),
	},
});

const slidingLayoutContainerInnerStyle = css({
	margin: 'auto',
	width: '1100px',
	maxWidth: '100%',
});

const slidingLayoutStyles = css({
	width: '100%',
	minHeight: 'var(--sliding-layout-container-height)',
	position: 'relative',
	display: 'grid',
	gap: token('space.300'),
	gridTemplateColumns: 'repeat(3, 1fr)',
	gridTemplateRows: 'repeat(2, 1fr)',
	['&.is-switching']: {
		display: 'block',
	},
	['&.featured-side_right']: {
		gridTemplateAreas: `
            "smallTop featured featured"
            "smallBottom featured featured"
        `,
	},
	['&.featured-side_left']: {
		gridTemplateAreas: `
            "featured featured smallTop"
            "featured featured smallBottom"
        `,
	},
	[media.below.md]: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		height: 'auto',
		minHeight: '0',
	},
});

const cardStyles = css({
	position: 'relative',
	transitionProperty: 'left, top, width, height',
	transitionDuration: '250ms',
	transitionTimingFunction: 'ease-in-out',
	borderRadius: wacToken('corner.radius.card'),
	['&.role_featured']: {
		gridArea: 'featured',
	},

	['&.role_smallTop']: {
		gridArea: 'smallTop',
	},
	['&.role_smallBottom']: {
		gridArea: 'smallBottom',
	},
	['&.is-animating']: {
		position: 'absolute',
		animationDuration: 'var(--sl-animation-duration)',
		animationTimingFunction: 'ease-in-out',
		animationFillMode: 'forwards',
		animationIterationCount: 1,
		pointerEvents: 'none',
		// featured cards shrinking to small cards
		['&.participant_featured.clicked-role_smallTop.direction_left']: {
			animationName: featuredFromRightToRightSmallTop,
		},
		['&.participant_featured.clicked-role_smallBottom.direction_left']: {
			animationName: featuredFromRightToRightSmallBottom,
		},
		['&.participant_featured.clicked-role_smallTop.direction_right']: {
			animationName: featuredFromLeftToLeftSmallTop,
		},
		['&.participant_featured.clicked-role_smallBottom.direction_right']: {
			animationName: featuredFromLeftToLeftSmallBottom,
		},
		// small cards expanding to featured cards
		['&.participant_clicked.clicked-role_smallTop.direction_left']: {
			animationName: smallTopFromLeftSmallToLeftFeatured,
		},
		['&.participant_clicked.clicked-role_smallBottom.direction_left']: {
			animationName: smallBottomFromLeftSmallToLeftFeatured,
		},
		['&.participant_clicked.clicked-role_smallTop.direction_right']: {
			animationName: smallTopFromRightSmallToRightFeatured,
		},
		['&.participant_clicked.clicked-role_smallBottom.direction_right']: {
			animationName: smallBottomFromRightSmallToRightFeatured,
		},
		// "other" small card expanding and then shrinking to the opposite side
		['&.participant_other.clicked-role_smallTop.direction_left']: {
			animationName: smallBottomFromLeftToRight,
		},
		['&.participant_other.clicked-role_smallBottom.direction_left']: {
			animationName: smallTopFromLeftToRight,
		},
		['&.participant_other.clicked-role_smallTop.direction_right']: {
			animationName: smallBottomFromRightToLeft,
		},
		['&.participant_other.clicked-role_smallBottom.direction_right']: {
			animationName: smallTopFromRightToLeft,
		},
		['&.participant_clicked']: {
			zIndex: 4,
		},
		['&.participant_featured']: {
			zIndex: 3,
		},
		['&.participant_other']: {
			zIndex: 2,
		},
	},

	[media.below.md]: {
		position: 'relative',
		width: '100%',
		insetInlineStart: 'auto',
		insetBlockStart: 'auto',
		height: 'auto',
		transition: 'none',
		animationName: 'none',
		minHeight: '0',
	},
	['&.small']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['.visitor']: {
			display: 'none',
			[media.below.md]: {
				display: 'block',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['.expander']: {
			[media.below.md]: {
				display: 'none',
			},
		},
	},
	['&.featured']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['.expander']: {
			display: 'none',
		},
	},
	['&.style_polygonBackgroundColor']: {
		borderRadius: 0,
		minHeight: '260px',
		[media.below.md]: {
			minHeight: '0',
		},
		['&.card0']: {
			clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) calc(100% - 15px), 15px 100%)',
		},
		['&.card1']: {
			clipPath: 'polygon(0 15px, 100% 0, 100% calc(100% - 15px), 0 100%)',
		},
		['&.card2']: {
			clipPath: 'polygon(15px 0, 100% 15px, calc(100% - 15px) 100%, 0 calc(100% - 15px))',
		},
	},

	['&.brightness_dark']: {
		color: wacToken('color.neutral.light0'),
		['&.style_none, &.style_borderColor, &.style_buttonColor']: {
			backgroundColor: wacToken('color.neutral.light1000'),
		},
	},
	['&.brightness_light']: {
		color: wacToken('color.primary.black'),
		['&.style_none, &.style_borderColor, &.style_buttonColor']: {
			backgroundColor: wacToken('color.neutral.light0'),
		},
		['&.style_none, &.style_buttonColor']: {
			transition: 'box-shadow 0.2s ease',
			borderStyle: 'solid',
			borderWidth: '1px',
			borderColor: wacToken('color.neutral.light400'),
			boxShadow: token('elevation.shadow.raised'),
			['&.small:hover']: {
				boxShadow: token('elevation.shadow.overlay'),
			},
		},
	},
	['&.style_borderColor']: {
		borderColor: 'var(--sliding-layout-card-color)',
		outlineColor: 'var(--sliding-layout-card-color)',
		borderWidth: '2px',
		borderStyle: 'solid',
		outlineWidth: '0px',
		outlineStyle: 'solid',
		transition: 'outline-width ease-in-out 200ms',
		['&.small:hover']: {
			outlineWidth: '2px',
		},
	},
	['&.style_backgroundColor, &.style_polygonBackgroundColor']: {
		backgroundColor: 'var(--sliding-layout-card-color)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['.buttonelement']: {
		display: 'inline-block',
		cursor: 'pointer',
		appearance: 'none',
		lineHeight: 1,
		borderStyle: 'solid',
		transition: 'background-color 0.2s ease, color 0.2s ease',
		['&.expander']: {
			aspectRatio: '1 / 1',
			borderRadius: '50%',
			padding: '10px',
			fontSize: '20px',
			borderWidth: '2px',
		},
		['&.visitor']: {
			fontSize: '12px',
			borderRadius: '30px',
			padding: '5px 10px',
			borderWidth: '1px',
		},
		['&.style_buttonColor']: {
			borderColor: 'var(--sliding-layout-card-color)',
			backgroundColor: 'var(--sliding-layout-card-color)',
			color: wacToken('color.neutral.light0'),
			['&:hover']: {
				color: 'var(--sliding-layout-card-color)',
			},
		},
		['&.style_none, &.style_borderColor, &.style_backgroundColor, &.style_polygonBackgroundColor']:
			{
				backgroundColor: 'transparent',
				borderColor: 'currentColor',
			},
		['&.style_backgroundColor, &.style_polygonBackgroundColor, &.style_buttonColor']: {
			['&:hover']: {
				color: 'var(--sliding-layout-card-color)',
			},
		},
		['&.brightness_light']: {
			['&.style_none, &.style_borderColor, &.style_backgroundColor, &.style_polygonBackgroundColor']:
				{
					color: wacToken('color.primary.black'),
				},
			['&.style_none, &.style_borderColor']: {
				['&:hover']: {
					color: wacToken('color.neutral.light0'),
					backgroundColor: wacToken('color.primary.black'),
				},
			},
			['&.style_backgroundColor, &.style_polygonBackgroundColor']: {
				['&:hover']: {
					backgroundColor: wacToken('color.primary.black'),
					color: 'var(--sliding-layout-card-color)',
				},
			},
			['&.style_buttonColor']: {
				['&:hover']: {
					backgroundColor: wacToken('color.neutral.light0'),
				},
			},
		},
		['&.brightness_dark']: {
			['&.style_none, &.style_borderColor, &.style_backgroundColor, &.style_polygonBackgroundColor']:
				{
					color: wacToken('color.neutral.light0'),
				},
			['&.style_none, &.style_borderColor']: {
				['&:hover']: {
					color: wacToken('color.neutral.light1000'),
					backgroundColor: wacToken('color.neutral.light0'),
				},
			},
			['&.style_backgroundColor, &.style_polygonBackgroundColor']: {
				['&:hover']: {
					backgroundColor: wacToken('color.neutral.light0'),
					color: 'var(--sliding-layout-card-color)',
				},
			},
			['&.style_buttonColor']: {
				['&:hover']: {
					backgroundColor: wacToken('color.neutral.light1000'),
				},
			},
		},
		['&disabled']: {
			opacity: 1,
			color: 'inherit',
			background: 'inherit',
		},
	},

	// card hover (to mimic button hover)
	['&:hover']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['.expander']: {
			['&.brightness_light']: {
				['&.style_none, &.style_borderColor']: {
					backgroundColor: wacToken('color.primary.black'),
					color: wacToken('color.neutral.light0'),
				},
				['&.style_backgroundColor, &.style_polygonBackgroundColor']: {
					backgroundColor: wacToken('color.primary.black'),
					color: 'var(--sliding-layout-card-color)',
				},
				['&.style_buttonColor']: {
					backgroundColor: wacToken('color.neutral.light0'),
					color: 'var(--sliding-layout-card-color)',
				},
			},
			['&.brightness_dark']: {
				['&.style_none, &.style_borderColor']: {
					backgroundColor: wacToken('color.neutral.light0'),
					color: wacToken('color.primary.black'),
				},
				['&.style_backgroundColor, &.style_polygonBackgroundColor']: {
					backgroundColor: wacToken('color.neutral.light0'),
					color: 'var(--sliding-layout-card-color)',
				},
				['&.style_buttonColor']: {
					backgroundColor: wacToken('color.neutral.light1000'),
					color: 'var(--sliding-layout-card-color)',
				},
			},
		},
	},
});

const clickableElementStyle = css({
	border: 'none',
	position: 'absolute',
	inset: 0,
	background: 'transparent',
	padding: '0',
	cursor: 'pointer',
	['&.featured']: {
		display: 'none',
	},
	[media.below.md]: {
		display: 'none',
	},
});

const cardInnerStyle = css({
	position: 'absolute',
	width: '100%',
	height: '100%',
	[media.below.md]: {
		position: 'relative',
	},
});

const cardPadding = css({
	padding: token('space.300'),
	['&.style_polygonBackgroundColor']: {
		padding: token('space.600'),
	},
});

const cardContentStyle = css({
	transformOrigin: '0 0',
	transition: 'transform 0.3s ease-in-out',
	maxWidth: 'calc(var(--sliding-layout-container-width) * 0.25)',
	[media.below.md]: {
		maxWidth: 'calc(var(--sliding-layout-container-width) * 0.5)',
	},
	['&.featured']: {
		transform: 'scale(2)',
		[media.below.md]: {
			transform: 'scale(1)',
		},
	},
});

const cardTitleStyle = css({
	lineHeight: '1.2',
	fontWeight: 800,
	fontSize: token('space.300'),
	margin: `0 0 ${token('space.200')}`,
});

const getCardColor = (
	color: SlidingLayoutCardColor,
	brightnessMode: SlidingLayoutBrightnessMode,
	colorMode: SlidingLayoutColorMode,
) => {
	const tree = {
		default: {
			light: {
				none: '',
				backgroundColor: wacToken('color.neutral.light200'),
				polygonBackgroundColor: wacToken('color.neutral.light200'),
				borderColor: wacToken('color.neutral.light0'),
				buttonColor: wacToken('color.neutral.light0'),
			},
			dark: {
				none: '',
				backgroundColor: wacToken('color.neutral.light1000'),
				polygonBackgroundColor: wacToken('color.neutral.light1000'),
				borderColor: wacToken('color.neutral.light1000'),
				buttonColor: wacToken('color.neutral.light1000'),
			},
		},
		purple: {
			light: {
				none: '',
				backgroundColor: wacToken('color.extended.purple200'),
				polygonBackgroundColor: wacToken('color.extended.purple200'),
				borderColor: wacToken('color.extended.purple500'),
				buttonColor: wacToken('color.extended.purple500'),
			},
			dark: {
				none: '',
				backgroundColor: wacToken('color.extended.purple500'),
				polygonBackgroundColor: wacToken('color.extended.purple500'),
				borderColor: wacToken('color.extended.purple500'),
				buttonColor: wacToken('color.extended.purple500'),
			},
		},
		saffron: {
			light: {
				none: '',
				backgroundColor: wacToken('color.extended.saffron200'),
				polygonBackgroundColor: wacToken('color.extended.saffron200'),
				borderColor: wacToken('color.extended.saffron500'),
				buttonColor: wacToken('color.extended.saffron500'),
			},
			dark: {
				none: '',
				backgroundColor: wacToken('color.extended.saffron600'),
				polygonBackgroundColor: wacToken('color.extended.saffron600'),
				borderColor: wacToken('color.extended.saffron600'),
				buttonColor: wacToken('color.extended.saffron600'),
			},
		},
		lime: {
			light: {
				none: '',
				backgroundColor: wacToken('color.extended.lime200'),
				polygonBackgroundColor: wacToken('color.extended.lime200'),
				borderColor: wacToken('color.extended.lime500'),
				buttonColor: wacToken('color.extended.lime500'),
			},
			dark: {
				none: '',
				backgroundColor: wacToken('color.extended.lime500'),
				polygonBackgroundColor: wacToken('color.extended.lime500'),
				borderColor: wacToken('color.extended.lime500'),
				buttonColor: wacToken('color.extended.lime500'),
			},
		},
		blue: {
			light: {
				none: '',
				backgroundColor: wacToken('color.extended.blue200'),
				polygonBackgroundColor: wacToken('color.extended.blue200'),
				borderColor: wacToken('color.extended.blue500'),
				buttonColor: wacToken('color.extended.blue700'),
			},
			dark: {
				none: '',
				backgroundColor: wacToken('color.extended.blue700'),
				polygonBackgroundColor: wacToken('color.extended.blue700'),
				borderColor: wacToken('color.extended.blue500'),
				buttonColor: wacToken('color.extended.blue700'),
			},
		},
	};
	const palette = tree[color] ?? tree.default;
	return palette[brightnessMode][colorMode];
};

export const SlidingLayout = ({
	cards,
	brightnessMode = 'light',
	colorMode = 'none',
}: SlidingLayoutProps) => {
	const intl = useIntl();
	const normalizedCards: SlidingLayoutCard[] = [0, 1, 2].map((index) => ({
		title: cards[index]?.title ?? '',
		color: cards[index]?.color ?? 'default',
		ctaLinkUrl: cards[index]?.ctaLinkUrl ?? '#',
		ctaLinkText: cards[index]?.ctaLinkText ?? 'Visit link',
		icon: cards[index]?.icon,
	}));

	const [cardRoles, setCardRoles] = useState<SlidingLayoutCardArea[]>([
		'smallTop',
		'smallBottom',
		'featured',
	]);
	const [featuredSide, setFeaturedSide] = useState<FeaturedSide>('right');
	const [isSwitching, setIsSwitching] = useState(false);
	const [animationContext, setAnimationContext] = useState<AnimationContext | null>(null);
	const [containerWidthPx, setContainerWidthPx] = useState(0);
	const animationTimeoutRef = useRef<number | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const clearAnimationTimer = () => {
		if (animationTimeoutRef.current !== null) {
			clearTimeout(animationTimeoutRef.current);
			animationTimeoutRef.current = null;
		}
	};

	useEffect(() => {
		return clearAnimationTimer;
	}, []);

	useEffect(() => {
		const containerElement = containerRef.current;
		if (!containerElement) {
			return;
		}

		const updateContainerWidth = () => {
			setContainerWidthPx(containerElement.getBoundingClientRect().width);
		};

		updateContainerWidth();
		const resizeObserver = new ResizeObserver(updateContainerWidth);
		resizeObserver.observe(containerElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const handleExpand = (index: number) => {
		if (isSwitching || cardRoles[index] === 'featured') {
			return;
		}

		const currentFeaturedIndex = cardRoles.findIndex((role) => role === 'featured');
		if (currentFeaturedIndex === -1) {
			return;
		}

		const clickedRole = cardRoles[index];
		if (!clickedRole) {
			return;
		}

		const otherSmallIndex = cardRoles.findIndex(
			(role, cardIndex) => role !== 'featured' && cardIndex !== index,
		);
		const otherSmallRole = cardRoles[otherSmallIndex];
		if (otherSmallIndex === -1 || !otherSmallRole || otherSmallRole === 'featured') {
			return;
		}

		const nextFeaturedSide: FeaturedSide = featuredSide === 'left' ? 'right' : 'left';
		clearAnimationTimer();
		setIsSwitching(true);
		setAnimationContext({
			clickedIndex: index,
			featuredIndex: currentFeaturedIndex,
			otherIndex: otherSmallIndex,
			clickedRole,
			otherRole: otherSmallRole,
			nextFeaturedSide,
		});
		animationTimeoutRef.current = window.setTimeout(() => {
			setCardRoles((currentRoles) => {
				const nextRoles = [...currentRoles];
				nextRoles[index] = 'featured';
				nextRoles[currentFeaturedIndex] = clickedRole;
				return nextRoles;
			});
			setFeaturedSide(nextFeaturedSide);
			setAnimationContext(null);
			setIsSwitching(false);
			clearAnimationTimer();
		}, SWITCH_DELAY_MS);
	};

	const containerHeightPx = colorMode === 'polygonBackgroundColor' ? 544 : 484;

	const getCardArea = (index: number): SlidingLayoutCardArea => cardRoles[index] ?? 'smallTop';

	const getAnimationParticipant = (index: number): AnimationParticipant | undefined => {
		if (!animationContext) {
			return undefined;
		}
		if (index === animationContext.clickedIndex) {
			return 'clicked';
		}
		if (index === animationContext.featuredIndex) {
			return 'featured';
		}
		if (index === animationContext.otherIndex) {
			return 'other';
		}
		return undefined;
	};

	return (
		<section
			className={`style_${colorMode} brightness_${brightnessMode}`}
			css={slidingLayoutSectionStyle}
		>
			<div css={slidingLayoutContainerInnerStyle}>
				<div
					ref={containerRef}
					className={`sliding-layout-container style_${colorMode} featured-side_${featuredSide} ${
						isSwitching ? 'is-switching' : ''
					}`}
					css={slidingLayoutStyles}
					style={
						{
							'--sliding-layout-container-width': `${containerWidthPx}px`,
							'--sliding-layout-container-height': `${containerHeightPx}px`,
							'--sliding-layout-gap': CONTAINER_GAP_PX,
							'--sl-small-width':
								'calc((var(--sliding-layout-container-width) - (2 * var(--sliding-layout-gap))) / 3)',
							'--sl-featured-width':
								'calc((2 * var(--sl-small-width)) + var(--sliding-layout-gap))',
							'--sl-small-height':
								'calc((var(--sliding-layout-container-height) - var(--sliding-layout-gap)) / 2)',
							'--sl-height-featured': 'var(--sliding-layout-container-height)',
							'--sl-left-featured-left': '0px',
							'--sl-left-featured-right':
								'calc(var(--sliding-layout-container-width) - var(--sl-featured-width))',
							'--sl-left-small-on-right-side':
								'calc(var(--sliding-layout-container-width) - var(--sl-small-width))',
							'--sl-left-small-on-left-side': '0px',
							'--sl-top-featured': '0px',
							'--sl-top-small-top': '0px',
							'--sl-top-small-bottom': 'calc(var(--sl-small-height) + var(--sliding-layout-gap))',
							'--sl-animation-duration': `${SWITCH_DELAY_MS}ms`,
						} as CSSProperties
					}
				>
					{normalizedCards.map((card, index) => {
						const cardArea = getCardArea(index);
						const isFeatured = cardArea === 'featured';
						const participant = getAnimationParticipant(index);
						const isAnimating = Boolean(participant && animationContext);
						const directionClass = animationContext ? animationContext.nextFeaturedSide : 'none';
						const clickedRoleClass = animationContext ? animationContext.clickedRole : 'none';

						const cardStyle: CSSProperties & Record<string, string> = {
							'--sliding-layout-card-color': getCardColor(
								card.color ?? 'default',
								brightnessMode,
								colorMode,
							),
						};

						return (
							<div
								key={card.title}
								className={`card${index} switching_${isSwitching} style_${colorMode} brightness_${brightnessMode} color_${
									card.color
								} side_${featuredSide} role_${cardArea} ${
									isFeatured && !isSwitching ? 'featured' : 'small'
								} ${isAnimating ? 'is-animating' : ''} ${
									participant ? `participant_${participant}` : ''
								} clicked-role_${clickedRoleClass} direction_${directionClass}`}
								style={cardStyle}
								css={[cardStyles]}
							>
								<div className={'card-inner' + index} css={cardInnerStyle}>
									<div className={`card-padding style_${colorMode}`} css={cardPadding}>
										<div
											css={cardContentStyle}
											className={isFeatured && !isSwitching ? 'featured' : 'small'}
										>
											{card.icon && (
												<Picture
													image={{
														width: 30,
														height: 30,
														src: card.icon,
														alt: '',
													}}
												/>
											)}
											<h3 css={cardTitleStyle}>{card.title}</h3>

											<span
												className={`buttonelement expander style_${colorMode} brightness_${brightnessMode}`}
											>
												＋
											</span>
											<button
												disabled={isSwitching}
												className={`clickable-element ${isFeatured ? 'featured' : 'small'}`}
												css={clickableElementStyle}
												onClick={() => handleExpand(index)}
												aria-label={intl.formatMessage({
													id: 'adg4.sliding-layout.expand-button-aria-label',
													defaultMessage: 'Expand',
												})}
											/>

											<a
												className={`buttonelement visitor style_${colorMode} brightness_${brightnessMode} color_${card.color}`}
												href={card.ctaLinkUrl}
											>
												{card.ctaLinkText}
											</a>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
};
